import { ReactNode, useMemo } from 'react';
import {
  getCumulativeLtcPayoutSeries,
  getFirstPayoutMonthIndex,
} from '@shared/calculations/funding/projection/projectionTotals';
import { FundingPolicyType } from '@shared/appModel';
import { buildPayoutColumnGroups } from './projectionTable/columnGroups';
import { ProjectionTable } from './projectionTable/ProjectionTable';
import { PrePayoutTable } from './projectionTable/PrePayoutTable';
import { getPresentation } from './projectionTable/strategies/registry';

interface MonthlyBreakdownTableProps {
  projection: ClaimProjection;
  policyType: FundingPolicyType | null | undefined;
}

/**
 * Partitions the projection at the first paying month and renders the two
 * halves inside one container with a timeline-style visual: a left-edge
 * rail with marker dots, the "Before benefits begin" section, a
 * "BENEFITS ACTIVATED" pill divider, then the "After benefits begin" section.
 */
export function MonthlyBreakdownTable({ projection, policyType }: MonthlyBreakdownTableProps) {
  const firstPayoutIdx = useMemo(
    () => getFirstPayoutMonthIndex(projection.months),
    [projection.months],
  );
  const splitIndex = firstPayoutIdx ?? projection.months.length;
  const preMonths = projection.months.slice(0, splitIndex);
  const payoutMonths = projection.months.slice(splitIndex);

  const presentation = useMemo(() => getPresentation(policyType), [policyType]);

  const payoutGroups = useMemo(
    () =>
      buildPayoutColumnGroups({
        terms: projection.terms,
        yearSnapshots: projection.yearSnapshots,
        presentation,
      }),
    [projection.terms, projection.yearSnapshots, presentation],
  );

  // Opening state going into the first payout-side month. If there is a
  // pre-payout slice, this is the close of its last month; otherwise it's
  // the initial state (= first month's openState).
  const initialState: MonthlyState =
    projection.months.length > 0
      ? projection.months[0].openState
      : {
          pools: { baseBenefitPool: 0, extensionOfBenefitsPool: 0, deathBenefit: 0 },
          waitingPeriod: { homeDays: 0, facilityDays: 0 },
          strategyState: {},
        };
  const payoutOpenState: MonthlyState =
    preMonths.length > 0 ? preMonths[preMonths.length - 1].state : initialState;

  // Cumulative LTC payout BEFORE the payout-side view starts. This is the
  // running sum across all pre-payout months — usually 0, but non-zero on
  // policies where the EP retroactive lump on a WP-serving month is the
  // event that crosses the firstPayoutMonth threshold.
  const cumulativeBeforePayoutView = useMemo(() => {
    if (preMonths.length === 0) return 0;
    const series = getCumulativeLtcPayoutSeries(preMonths);
    return series[series.length - 1];
  }, [preMonths]);

  const hasPre = preMonths.length > 0;
  const hasPayout = payoutMonths.length > 0;
  const showDivider = hasPre && hasPayout;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">Monthly breakdown</h2>
      </div>
      {hasPre && (
        <TimelineSection
          marker="pre"
          badge={
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
              Out-of-pocket period
            </span>
          }
        >
          <PrePayoutTable months={preMonths} initialState={initialState} />
        </TimelineSection>
      )}
      {hasPayout && (
        <TimelineSection
          marker="post"
          continuesRail={showDivider}
          badge={
            hasPre ? (
              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                Benefits activated
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                Month-by-month
              </span>
            )
          }
        >
          <ProjectionTable
            groups={payoutGroups}
            months={payoutMonths}
            openStateForFirstMonth={payoutOpenState}
            cumulativeLtcPayoutBeforeFirstMonth={cumulativeBeforePayoutView}
            groupByPolicyYear
          />
        </TimelineSection>
      )}
    </div>
  );
}

function TimelineSection({
  marker,
  badge,
  subtitle,
  continuesRail,
  children,
}: {
  marker: 'pre' | 'post';
  badge: ReactNode;
  subtitle?: string;
  continuesRail?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="relative pl-10">
      <span
        className={`absolute left-5 w-px bg-gray-200 ${continuesRail ? '-top-2' : 'top-5'} bottom-0`}
        aria-hidden
      />
      <span
        className={`absolute left-[14px] top-5 inline-flex h-3 w-3 items-center justify-center rounded-full ring-2 ring-white ${
          marker === 'post' ? 'bg-emerald-500' : 'border-2 border-gray-400 bg-white'
        }`}
        aria-hidden
      />
      <div className="px-2 py-4">
        <div className="mb-3 flex items-baseline gap-2">
          {badge}
          {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
        </div>
        {children}
      </div>
    </section>
  );
}
